import { gql } from "@apollo/client";

// ──────────────────────────────────────────────────────────────────────────
// Shared scalar types
// ──────────────────────────────────────────────────────────────────────────

export interface AcDataScope {
  requireIpaMatch: boolean | null;
  allowedLineOfBusiness: string[] | null;
}

// AcPersonaModule — used inside currentUser.currentPersona (no isActive field)
export interface AcPersonaModule {
  moduleId: string;
  sortOrder: number;
  actions: string[] | null;
  dataScope: AcDataScope | null;
}

// AcAdminPersonaModule — used in listPersonas (has isActive)
export interface AcAdminPersonaModule {
  moduleId: string;
  sortOrder: number;
  actions: string[] | null;
  dataScope: AcDataScope | null;
  isActive: boolean;
}

export interface AcAdminPersona {
  code: string;
  displayName: string;
  modules: AcAdminPersonaModule[];
}

// AcPersonaSummary — used in currentUser.personas[] (no modules)
export interface AcPersonaSummary {
  code: string;
  displayName: string;
}

// AcCurrentPersona — used in currentUser.currentPersona (has modules, no isActive)
export interface AcCurrentPersona {
  code: string;
  displayName: string;
  modules: AcPersonaModule[];
}

export interface AcRole {
  code: string;
  displayName: string;
}

export interface AcPermissionMeta {
  ipas: string[] | null;
  healthPlans: string[] | null;
  lineOfBusiness: string[] | null;
}

export interface AcPermission {
  value: string;
  permissionType: string;
  permissionMeta: AcPermissionMeta | null;
}

export interface AcCurrentUser {
  id: string;
  email: string;
  displayName: string | null;
  role: AcRole | null;
  // personas[] is AcPersonaSummary — NO modules field on this list
  personas: AcPersonaSummary[];
  // currentPersona has modules (AcPersonaModule), dataScope is an object
  currentPersona: AcCurrentPersona | null;
  permissions: AcPermission[];
}

// AcUserSummary — returned by listUsers
export interface AcUserSummary {
  id: string;
  email: string;
  displayName: string | null;
  status: string;
  role: AcRole | null;
  personas: AcPersonaSummary[];
}

// ──────────────────────────────────────────────────────────────────────────
// Reusable fragments
// ──────────────────────────────────────────────────────────────────────────

// dataScope subfields — AcDataScope object (NOT a scalar string)
const DATA_SCOPE_FIELDS = `
  dataScope {
    requireIpaMatch
    allowedLineOfBusiness
  }
`;

// currentUser full payload — reused in mutation return selections
const CURRENT_USER_FIELDS = `
  id
  email
  displayName
  role {
    code
    displayName
  }
  personas {
    code
    displayName
  }
  currentPersona {
    code
    displayName
    modules {
      moduleId
      sortOrder
      actions
      ${DATA_SCOPE_FIELDS}
    }
  }
  permissions {
    value
    permissionType
    permissionMeta {
      ipas
      healthPlans
      lineOfBusiness
    }
  }
`;

// ──────────────────────────────────────────────────────────────────────────
// Query: currentUser
// ──────────────────────────────────────────────────────────────────────────

export const CURRENT_USER_QUERY = gql`
  query AcCurrentUser {
    currentUser {
      ${CURRENT_USER_FIELDS}
    }
  }
`;

export interface CurrentUserResult {
  currentUser: AcCurrentUser | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Query: listUsers  (AC-ADM-1)
// ──────────────────────────────────────────────────────────────────────────

export const LIST_USERS_QUERY = gql`
  query AcListUsers($input: AcListUsersInput) {
    listUsers(input: $input) {
      items {
        id
        email
        displayName
        status
        role {
          code
          displayName
        }
        personas {
          code
          displayName
        }
      }
      pageInformation {
        totalRecords
      }
    }
  }
`;

// Mirrors the BE AcListUsersInput (care-management-gql AcAdminResolver.listUsers).
// page is 0-indexed; pageSize is capped at 200 (MAX_USERS_PAGE_SIZE) server-side.
export interface ListUsersInput {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  roleCode?: string;
  personaCode?: string;
}

export interface ListUsersResult {
  listUsers: {
    items: AcUserSummary[];
    pageInformation: { totalRecords: number | null } | null;
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Query: listRoles  (AC-ADM-1 — role filter catalog for the Users page)
// ──────────────────────────────────────────────────────────────────────────

export const LIST_ROLES_QUERY = gql`
  query AcListRoles {
    listRoles {
      code
      displayName
    }
  }
`;

export interface ListRolesResult {
  listRoles: { code: string; displayName: string }[];
}

// ──────────────────────────────────────────────────────────────────────────
// Query: listPersonas  (AC-ADM-3)
// AcAdminPersona.modules is AcAdminPersonaModule (has isActive)
// ──────────────────────────────────────────────────────────────────────────

export const LIST_PERSONAS_QUERY = gql`
  query AcListPersonas {
    listPersonas {
      code
      displayName
      modules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
    }
  }
`;

export interface ListPersonasResult {
  listPersonas: AcAdminPersona[];
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: createUser  (AC-ADM-1)
// Returns AcCurrentUser (full payload)
// ──────────────────────────────────────────────────────────────────────────

export const CREATE_USER_MUTATION = gql`
  mutation AcCreateUser(
    $email: String!
    $displayName: String
    $roleCodes: [String!]
    $personaCodes: [String!]
  ) {
    createUser(
      email: $email
      displayName: $displayName
      roleCodes: $roleCodes
      personaCodes: $personaCodes
    ) {
      ${CURRENT_USER_FIELDS}
    }
  }
`;

export interface CreateUserVars {
  email: string;
  displayName?: string;
  roleCodes?: string[];
  personaCodes?: string[];
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: updateUser  (AC-ADM-1)
// Returns AcCurrentUser (full payload)
// ──────────────────────────────────────────────────────────────────────────

export const UPDATE_USER_MUTATION = gql`
  mutation AcUpdateUser($userId: String!, $displayName: String, $status: String) {
    updateUser(userId: $userId, displayName: $displayName, status: $status) {
      id
      email
      displayName
    }
  }
`;

export interface UpdateUserVars {
  userId: string;
  displayName?: string;
  status?: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: suspendUser  (AC-ADM-1)
// Returns AcCurrentUser (full payload — we just need id/email/status)
// ──────────────────────────────────────────────────────────────────────────

export const SUSPEND_USER_MUTATION = gql`
  mutation AcSuspendUser($userId: String!) {
    suspendUser(userId: $userId) {
      id
      email
    }
  }
`;

export interface SuspendUserVars {
  userId: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: assignPersonas  (AC-ADM-3)
// Returns AcCurrentUser
// ──────────────────────────────────────────────────────────────────────────

export const ASSIGN_PERSONAS_MUTATION = gql`
  mutation AcAssignPersonas($userId: String!, $personaCodes: [String!]!) {
    assignPersonas(userId: $userId, personaCodes: $personaCodes) {
      id
      personas {
        code
        displayName
      }
    }
  }
`;

export interface AssignPersonasVars {
  userId: string;
  personaCodes: string[];
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: setCurrentPersona  (Screen 3)
// Returns AcCurrentUser
// ──────────────────────────────────────────────────────────────────────────

export const SET_CURRENT_PERSONA_MUTATION = gql`
  mutation AcSetCurrentPersona($personaCode: String!) {
    setCurrentPersona(personaCode: $personaCode) {
      ${CURRENT_USER_FIELDS}
    }
  }
`;

export interface SetCurrentPersonaVars {
  personaCode: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: upsertPersonaModule  (AC-ADM-3)
// Returns AcPersonaModule (NOT AcAdminPersona).
// IMPORTANT: dataScope arg is a JSON string (serialized), not an object.
// The resolver stores it as jsonb; pass stringified JSON or undefined.
// ──────────────────────────────────────────────────────────────────────────

export const UPSERT_PERSONA_MODULE_MUTATION = gql`
  mutation AcUpsertPersonaModule(
    $personaCode: String!
    $moduleId: String!
    $sortOrder: Int!
    $actions: [String!]
    $dataScope: String
    $isActive: Boolean
  ) {
    upsertPersonaModule(
      personaCode: $personaCode
      moduleId: $moduleId
      sortOrder: $sortOrder
      actions: $actions
      dataScope: $dataScope
      isActive: $isActive
    ) {
      moduleId
      sortOrder
      actions
      ${DATA_SCOPE_FIELDS}
    }
  }
`;

export interface UpsertPersonaModuleVars {
  personaCode: string;
  moduleId: string;
  sortOrder: number;
  actions?: string[];
  dataScope?: string; // JSON string
  isActive?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: deactivatePersonaModule  (AC-ADM-3)
// care-management-gql returns a scalar Boolean (true on success) — NOT an
// AcPersonaModule object. Selecting object subfields would 400 against the
// care-mgmt schema, so this mutation has no selection set.
// ──────────────────────────────────────────────────────────────────────────

export const DEACTIVATE_PERSONA_MODULE_MUTATION = gql`
  mutation AcDeactivatePersonaModule($personaCode: String!, $moduleId: String!) {
    deactivatePersonaModule(personaCode: $personaCode, moduleId: $moduleId)
  }
`;

export interface DeactivatePersonaModuleVars {
  personaCode: string;
  moduleId: string;
}

export interface DeactivatePersonaModuleResult {
  deactivatePersonaModule: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: createUsers  (ACT-1293 — CSV bulk import)
//
// Input: CreateUserInput { email, displayName, roleCodes, personaCodes }
// Result per row: CreateUsersResult { email, success, error, user }
//
// Partial success: some rows can succeed while others fail.
// Backend max: 500 rows per call — caller must chunk if CSV > 500 rows.
// ──────────────────────────────────────────────────────────────────────────

export interface CreateUserInput {
  email: string;
  displayName?: string;
  roleCodes?: string[];
  personaCodes?: string[];
}

// Narrow user shape — the results table only needs id/email/displayName;
// requesting the full CURRENT_USER_FIELDS for up to 500 users is wasteful.
export interface CreateUsersUserSummary {
  id: string;
  email: string;
  displayName: string | null;
}

export interface CreateUsersRowResult {
  email: string;
  success: boolean;
  error: string | null;
  user: CreateUsersUserSummary | null;
}

export interface CreateUsersVars {
  users: CreateUserInput[];
}

export interface CreateUsersResult {
  createUsers: CreateUsersRowResult[];
}

export const CREATE_USERS_MUTATION = gql`
  mutation AcCreateUsers($users: [CreateUserInput!]!) {
    createUsers(users: $users) {
      email
      success
      error
      user {
        id
        email
        displayName
      }
    }
  }
`;


// ──────────────────────────────────────────────────────────────────────────
// Query: personaModuleOptions  (AC-ADM-3 — dropdown catalogs)
// Deterministic option lists for the Add/Edit Module modal: distinct-in-use ∪
// canonical defaults (moduleIds, lineOfBusiness), distinct-in-use actions, and all
// personas. Admin-gated on the backend.
// ──────────────────────────────────────────────────────────────────────────

export interface PersonaModuleOptions {
  moduleIds: string[];
  actions: string[];
  lineOfBusiness: string[];
  personas: AcPersonaSummary[];
}

export const PERSONA_MODULE_OPTIONS_QUERY = gql`
  query AcPersonaModuleOptions {
    personaModuleOptions {
      moduleIds
      actions
      lineOfBusiness
      personas {
        code
        displayName
      }
    }
  }
`;

export interface PersonaModuleOptionsResult {
  personaModuleOptions: PersonaModuleOptions;
}

// ──────────────────────────────────────────────────────────────────────────
// Feature Flags  (ACT-1235 — now REAL)
// ──────────────────────────────────────────────────────────────────────────

export interface AcFeatureFlag {
  key: string;
  displayName: string;
  description: string | null;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export const LIST_FEATURE_FLAGS_QUERY = gql`
  query AcListFeatureFlags {
    listFeatureFlags {
      key
      displayName
      description
      enabled
      updatedAt
      updatedBy
    }
  }
`;

export interface ListFeatureFlagsResult {
  listFeatureFlags: AcFeatureFlag[];
}

export const SET_FEATURE_FLAG_MUTATION = gql`
  mutation AcSetFeatureFlag($key: String!, $enabled: Boolean!) {
    setFeatureFlag(key: $key, enabled: $enabled) {
      key
      displayName
      description
      enabled
      updatedAt
      updatedBy
    }
  }
`;

export interface SetFeatureFlagVars {
  key: string;
  enabled: boolean;
}
